import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { EmblaCarouselType } from 'embla-carousel';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type PropType = ComponentPropsWithRef<'button'>;

export const PrevButton: React.FC<PropType> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button
      className="embla__button embla__button--prev"
      type="button"
      {...restProps}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="35"
        height="35"
        viewBox="0 0 35 35"
      >
        <g
          id="Icon_feather-arrow-left-circle"
          data-name="Icon feather-arrow-left-circle"
          transform="translate(-2 -2)"
        >
          <path
            id="Path_7968"
            data-name="Path 7968"
            d="M36,19.5A16.5,16.5,0,1,1,19.5,3,16.5,16.5,0,0,1,36,19.5Z"
            transform="translate(0)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <path
            id="Path_7969"
            data-name="Path 7969"
            d="M18,12l-6,6,6,6"
            transform="translate(1.125 1.5)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <path
            id="Path_7970"
            data-name="Path 7970"
            d="M24,18H12"
            transform="translate(1.5 1.5)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </g>
      </svg>

      {children}
    </button>
  );
};

export const NextButton: React.FC<PropType> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button
      className="embla__button embla__button--next"
      type="button"
      {...restProps}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="35"
        height="35"
        viewBox="0 0 35 35"
      >
        <g
          id="Icon_feather-arrow-right-circle"
          data-name="Icon feather-arrow-right-circle"
          transform="translate(-2 -2)"
        >
          <path
            id="Path_7965"
            data-name="Path 7965"
            d="M36,19.5A16.5,16.5,0,1,1,19.5,3,16.5,16.5,0,0,1,36,19.5Z"
            transform="translate(0 0)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <path
            id="Path_7966"
            data-name="Path 7966"
            d="M18,25.2l6.6-6.6L18,12"
            transform="translate(1.5 0.9)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <path
            id="Path_7967"
            data-name="Path 7967"
            d="M12,18H25.2"
            transform="translate(0.9 1.5)"
            fill="none"
            stroke="#03b3d1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </g>
      </svg>

      {children}
    </button>
  );
};
